<template>
  <Container v-bind="containerProps">
    <template #leading>
      <button v-if="!slim" class="bg-transparent bg-gray-100 dark:bg-gray-900 hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors duration-200 m-1 ml-0 w-7 flex items-center justify-center h-7 rounded" :class="{ 'pointer-events-none' : disabled }" @click="decrease">
        <o-icon icon="subtract" :size="10" />
      </button>

      <slot name="leading" />
    </template>

    <input
      ref="input"
      v-model="currentValue"
      type="number"
      class="flex-grow px-2 h-full w-full bg-transparent text-sm"
      :max="max"
      :min="min"
      :step="step"
      v-bind="attributes"
      v-on="handlers"
      @input="handleInput"
    >

    <template #trailing>
      <slot name="trailing">
        <div v-if="trailing" class="pr-2 pl-2 opacity-80 leading-none text-base border-l ml-2 border-gray-200 dark:border-gray-800">
          <p class="leading-none text-sm">
            {{ trailing }}
          </p>
        </div>
      </slot>

      <button v-if="!slim" class="bg-transparent bg-gray-100 dark:bg-gray-900 hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors duration-200 m-1 mr-0 w-7 flex items-center justify-center h-7 rounded" :class="{ 'pointer-events-none' : disabled }" @click="increase">
        <o-icon icon="add" :size="10" />
      </button>

      <div v-if="slim" class="flex flex-col h-full divide-y border rounded">
        <button class="bg-gray-100 dark:bg-gray-800 hover:bg-blue-200 hover:dark:bg-gray-900 transition-colors duration-200 w-5 flex items-center justify-center h-1/2" :class="{ 'pointer-events-none' : disabled }" @click="increase">
          <o-icon icon="add" :size="8" />
        </button>
        <button class="bg-gray-100 dark:bg-gray-800 hover:bg-blue-200 hover:dark:bg-gray-900 transition-colors duration-200 w-5 flex items-center justify-center h-1/2" :class="{ 'pointer-events-none' : disabled }" @click="decrease">
          <o-icon icon="subtract" :size="8" />
        </button>
      </div>
    </template>
  </Container>
</template>

<script setup lang="ts">
import Container from './Container.vue'

interface Props {
  modelValue?: number | string
  increment?: number
  min?: number
  max?: number
  step?: string
  slim?: boolean
  id?: string | number | null
  name?: string | null
  required?: boolean
  readonly?: boolean
  clear?: boolean
  disabled?: boolean
  size?: string | number | null
  pattern?: string | null
  placeholder?: string | number | null
  leading?: string | string[] | null
  trailing?: string | string[] | null
  loading?: boolean
  baseClass?: string | Record<string, any> | string[]
  border?: string | Record<string, any> | string[] | boolean
  rounded?: string | Record<string, any> | string[] | boolean
  shadow?: string | Record<string, any> | string[] | boolean
  highlight?: string | Record<string, any> | string[] | boolean
  defaultStatusClass?: string | Record<string, any> | string[]
  warningStatusClass?: string | Record<string, any> | string[]
  errorStatusClass?: string | Record<string, any> | string[]
  successStatusClass?: string | Record<string, any> | string[]
  disabledClass?: string | Record<string, any> | string[]
  defaultSizeClass?: string | Record<string, any> | string[]
  largeSizeClass?: string | Record<string, any> | string[]
  smallSizeClass?: string | Record<string, any> | string[]
  status?: boolean | string | null
  autofocus?: boolean | null
  tabindex?: string | number | null
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: 0,
  increment: 1,
  min: 0,
  max: Number.MAX_SAFE_INTEGER,
  step: 'any',
  slim: false,
  id: null,
  name: null,
  required: false,
  readonly: false,
  clear: false,
  disabled: false,
  size: null,
  pattern: null,
  placeholder: null,
  leading: null,
  trailing: null,
  loading: false,
  baseClass: 'h-9 w-full relative flex items-center',
  border: 'border dark:border-gray-800',
  rounded: 'rounded-md',
  shadow: 'shadow-sm',
  highlight: 'focus-within:ring-2',
  defaultStatusClass: 'bg-white dark:bg-gray-900',
  warningStatusClass: 'border-yellow-200 bg-yellow-50 dark:bg-yellow-400/20',
  errorStatusClass: 'border-red-300 bg-red-100 dark:bg-red-400/20',
  successStatusClass: 'border-green-200 bg-green-100 dark:bg-green-400/20',
  disabledClass: 'bg-gray-50 dark:bg-gray-800 cursor-not-allowed opacity-75 dark:opacity-50',
  defaultSizeClass: 'p-1',
  largeSizeClass: 'p-4 text-lg',
  smallSizeClass: 'p-px text-sm',
  status: null,
  autofocus: null,
  tabindex: null,
})

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void
  (e: 'blur', value: any): void
  (e: 'focus', value: any): void
  (e: 'change', value: any): void
  (e: 'keyup', value: any): void
  (e: 'keydown', value: any): void
}>()

const input = ref<HTMLInputElement>()
const valueWhenFocus = ref<any>(null)

const currentValue = computed({
  get () {
    return props.modelValue
  },
  set (value: any) {
    if (typeof value === 'string' && value !== '') {
      value = Number(value)
    }
    emit('update:modelValue', value)
  },
})

const noStatus = computed(() => props.status === null)
const isError = computed(() => props.status === false || props.status === 'error')
const isSuccess = computed(() => props.status === true || props.status === 'success')
const isWarning = computed(() => props.status === 'warning')

const statusClasses = computed(() => {
  const classes: any[] = []
  if (props.disabled) {
    if (props.disabledClass) classes.push(props.disabledClass)
  }
  if (props.size === null && props.defaultSizeClass) classes.push(props.defaultSizeClass)
  else if (props.size === 'sm' && props.smallSizeClass) classes.push(props.smallSizeClass)
  else if (props.size === 'lg' && props.largeSizeClass) classes.push(props.largeSizeClass)
  if (!props.disabled && noStatus.value && props.defaultStatusClass) classes.push(props.defaultStatusClass)
  if (isError.value && props.errorStatusClass) classes.push(props.errorStatusClass)
  else if (isSuccess.value && props.successStatusClass) classes.push(props.successStatusClass)
  else if (isWarning.value && props.warningStatusClass) classes.push(props.warningStatusClass)
  return classes
})

const currentClass = computed(() => {
  const classes: any[] = [props.baseClass]
  return classes.concat(statusClasses.value)
})

const attributes = computed(() => ({
  id: props.id,
  name: props.name,
  required: props.required,
  readonly: props.readonly,
  clear: props.clear,
  disabled: props.disabled,
  size: props.size,
  pattern: props.pattern,
  placeholder: props.placeholder,
}))

const handlers = computed(() => ({
  blur: onBlur,
  focus: onFocus,
  keyup: onKeyUp,
  keydown: onKeyDown,
}))

const containerProps = computed(() => {
  const classes = [...currentClass.value]
  if (props.border) classes.push(props.border as string)
  if (props.rounded) classes.push(props.rounded as string)
  if (props.shadow) classes.push(props.shadow as string)
  if (props.highlight) classes.push(props.highlight as string)
  return {
    class: classes,
    baseClass: props.baseClass,
    leading: props.leading,
    trailing: props.trailing,
    loading: props.loading,
  }
})

function onBlur (e: Event) {
  emit('blur', e)
  if (currentValue.value !== valueWhenFocus.value) {
    emit('change', currentValue.value)
  }
}

function onFocus (e: Event) {
  emit('focus', e)
  valueWhenFocus.value = currentValue.value
}

function onKeyUp (e: Event) {
  emit('keyup', e)
}

function onKeyDown (e: Event) {
  emit('keydown', e)
}

function handleInput (e: Event) {
  const target = e.target as HTMLInputElement
  const value = Number(target.value)
  if (value < props.min) {
    target.value = String(props.min)
    currentValue.value = props.min
  } else if (value > props.max) {
    target.value = String(props.max)
    currentValue.value = props.max
  }
}

function increase () {
  const current = currentValue.value !== null ? currentValue.value : props.placeholder || null
  const value = Number(current) + props.increment

  if (value <= props.max) {
    currentValue.value = value
  }
}

function decrease () {
  const current = currentValue.value !== null ? currentValue.value : props.placeholder || null
  const value = Number(current) - props.increment

  if (props.min <= value) {
    currentValue.value = value
  }
}
</script>

<style>
  /* Chrome, Safari, Edge, Opera */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  input[type=number] {
    -moz-appearance: textfield;
  }
</style>
